import type { ReportFilter } from '../models/filter.types'
import type { ReportSpec } from '../models/report.types'

const DATE_DIMENSIONS = new Set([
  'date',
  'day_of_week',
  'week',
  'day_of_month',
  'day_of_year',
  'month_of_year',
])

const LIVE_REACH_FREQUENCY_PARTITIONS = new Set([
  'budget_allocation',
  'metrics_ad',
  'adgroup_budget_allocation',
])

export interface BuildReportQueryOptions {
  exportRequest?: boolean
  workspaceIds?: readonly (number | string)[]
}

function setJson(params: URLSearchParams, key: string, value: unknown): void {
  params.set(key, JSON.stringify(value))
}

function scalePercentage(value: unknown): unknown {
  if (typeof value === 'number') return value / 100
  if (Array.isArray(value)) return value.map((item) => scalePercentage(item))
  return value
}

function serializeFilter(filter: ReportFilter, scale = true): Record<string, unknown> {
  return {
    match_mode: filter.match_mode,
    field: filter.field,
    is_change: filter.is_change,
    direction: filter.direction,
    value:
      scale && filter.sub_type === 'percentage' && !filter.is_change
        ? scalePercentage(filter.value)
        : filter.value,
  }
}

function hasDateDimension(spec: ReportSpec): boolean {
  return (
    (spec.breakdown !== undefined && DATE_DIMENSIONS.has(spec.breakdown)) ||
    Boolean(spec.dimensions?.some((dimension) => DATE_DIMENSIONS.has(dimension)))
  )
}

function queryParams(defaultPostData: string): URLSearchParams {
  return new URLSearchParams(
    defaultPostData.startsWith('?') ? defaultPostData.slice(1) : defaultPostData,
  )
}

/**
 * Whether the picker's `compare_*` parameters survive into the controller query. Mirrors v3
 * `getPostObj` (`Orphex-New-Front/src/stores/reportStore.ts:283`): compare is kept by default and
 * `compare_type_not_prev` is an opt-OUT. The first port inverted it, so every ordinary report
 * table silently lost its comparison range and never received `prev_<metric>`.
 */
function keepsCompareParams(spec: ReportSpec): boolean {
  if (spec.add_compare) return true
  return !(spec.hide_compare || spec.compare_type_not_prev || hasDateDimension(spec))
}

function appendDefaultPostData(params: URLSearchParams, spec: ReportSpec): void {
  if (!spec.defaultPostData) return

  const keepCompare = keepsCompareParams(spec)
  for (const [key, value] of queryParams(spec.defaultPostData)) {
    if (!key.includes('compare')) {
      params.set(key, value)
      continue
    }
    if (!keepCompare) continue
    if (!spec.add_compare && !params.has('compare_type')) params.set('compare_type', 'prev')
    params.set(key, value)
  }
}

/**
 * True when the composed request actually carries the date picker's comparison range — the
 * "is a comparison selected?" signal chart-type availability needs (contract 08 §14). It reuses
 * `keepsCompareParams` so it can never drift from what `appendDefaultPostData` emits.
 *
 * v3 asked the same question through a `checkCompare` prop that `getAvailableChartTypes` then
 * dropped on the floor (`Orphex-New-Front/src/utils/helpers.js:171`), so no chart type was ever
 * gated on it.
 */
export function specRequestsComparison(spec: ReportSpec): boolean {
  if (!spec.defaultPostData || !keepsCompareParams(spec)) return false
  const params = queryParams(spec.defaultPostData)
  return Boolean(params.get('compare_date_start') && params.get('compare_date_end'))
}

function dimensionsForRequest(spec: ReportSpec): string[] {
  const edges = new Set(spec.edges ?? [])
  return (spec.dimensions ?? []).filter((dimension) => {
    if (dimension === spec.breakdown) return false
    if (edges.has('image_id') && dimension.includes('image')) return false
    if (edges.has('video_id') && (dimension.includes('video') || dimension.includes('thumbnail'))) {
      return false
    }
    return true
  })
}

/** The controller window when a request names none (v3 `OrphexReportTable.vue:924`). */
export const DEFAULT_CONTROLLER_ROWS = 250

function appendPaging(
  params: URLSearchParams,
  spec: ReportSpec,
  options: BuildReportQueryOptions,
): void {
  if (spec.partition_only) params.set('partition_only', 'true')
  // v3 sent a window on every controller read (`first`, `rows: rowCount || 250` in
  // `OrphexReportTable.getPostData`). Leaving them off — any unpaged table, every dashboard block
  // without a row count, every chart without a limit — let the controller apply no bound at all.
  params.set('first', String(spec.first ?? 0))
  if (options.exportRequest) params.set('rows', '10000')
  else params.set('rows', String(spec.rows ?? DEFAULT_CONTROLLER_ROWS))
  if (spec.pagination_enabled !== undefined) {
    params.set('pagination_enabled', String(spec.pagination_enabled))
  }
}

function appendFilters(params: URLSearchParams, spec: ReportSpec): void {
  if (spec.filters?.length) {
    setJson(
      params,
      'filters',
      spec.filters.filter(Boolean).map((filter) => serializeFilter(filter)),
    )
  }
  if (spec.change_filters?.length) {
    setJson(
      params,
      'change_filters',
      spec.change_filters.filter(Boolean).map((filter) => serializeFilter(filter, false)),
    )
  }
  if (spec.filter_logic !== undefined) setJson(params, 'filter_logic', spec.filter_logic)
}

function appendColumnGroups(
  params: URLSearchParams,
  spec: ReportSpec,
  options: BuildReportQueryOptions,
): void {
  // A read asks only WHAT TO GROUP BY: the controller answers with the partition's metrics either
  // way, and the renderers show the ones their columns select (v3 sent `metrics: null` for the
  // same reason). Naming them only split otherwise-identical reads into separate requests.
  // An export is the exception — its file is the selection, so it still names its metrics.
  if (options.exportRequest && spec.metrics?.length) setJson(params, 'metrics', spec.metrics)
  if (spec.count_dimensions?.length) setJson(params, 'count_dimensions', spec.count_dimensions)
  if (spec.group_dimensions?.length) setJson(params, 'group_dimensions', spec.group_dimensions)
  if (spec.count_unique_dimensions?.length) {
    setJson(params, 'count_unique_dimensions', spec.count_unique_dimensions)
  }
  if (spec.pivot_metric) params.set('pivot_metric', spec.pivot_metric)
  if (spec.grand_total_metrics) setJson(params, 'grand_total_metrics', spec.grand_total_metrics)
}

function appendSortAndPartition(params: URLSearchParams, spec: ReportSpec): void {
  if (spec.sort_field && (spec.sort_order === 1 || spec.sort_order === -1)) {
    params.set('sort_field', spec.sort_field)
    params.set('sort_order', String(spec.sort_order))
  }
  if (spec.partition_name) params.set('partition_name', spec.partition_name)
  if (
    spec.fetch_live_reach_frequency &&
    spec.partition_name &&
    LIVE_REACH_FREQUENCY_PARTITIONS.has(spec.partition_name)
  ) {
    params.set('fetch_live_reach_frequency', 'true')
  }
  if (!params.has('compare_type') && spec.compare_type) {
    params.set('compare_type', spec.compare_type)
  }
  if (spec.breakdown) params.set('breakdown', spec.breakdown)
}

function appendExportAndSpecialCases(params: URLSearchParams, spec: ReportSpec): void {
  if (spec.export_metric_key_names) {
    setJson(params, 'export_metric_key_names', spec.export_metric_key_names)
  }
  if (spec.export_dimension_key_names) {
    setJson(params, 'export_dimension_key_names', spec.export_dimension_key_names)
  }
  if (spec.heatmap !== undefined) {
    params.set(
      'heatmap',
      typeof spec.heatmap === 'string' ? spec.heatmap : JSON.stringify(spec.heatmap),
    )
  }
  if (spec.export_format) params.set('export_format', spec.export_format)
  if (spec.export_email) params.set('export_email', spec.export_email)
  if (spec.share_with?.length) setJson(params, 'share_with', spec.share_with)
  if (spec.target_currency) params.set('target_currency', spec.target_currency)
  if (spec.config_id !== undefined) params.set('config_id', String(spec.config_id))
  if (spec.edges?.length) setJson(params, 'edges', spec.edges)
}

/** Pure ReportSpec to controller query serializer (contract 08 section 6). */
export function buildReportQuery(spec: ReportSpec, options: BuildReportQueryOptions = {}): string {
  const params = new URLSearchParams()
  const workspaceIds = options.workspaceIds ?? []

  if (workspaceIds.length > 0) setJson(params, 'workspace_id', workspaceIds)
  appendDefaultPostData(params, spec)
  appendPaging(params, spec, options)
  appendFilters(params, spec)
  appendColumnGroups(params, spec, options)
  appendSortAndPartition(params, spec)
  appendExportAndSpecialCases(params, spec)

  const dimensions = dimensionsForRequest(spec)
  if (dimensions.length > 0) setJson(params, 'dimensions', dimensions)

  return params.toString()
}
